import PropTypes from 'prop-types';
import React from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  intlShape
} from 'react-intl';
import {
  normalizedContractsShape,
  priceBreakdownObject
} from '../../app-bookings-request/forms/bookingUtil';
import { BookingSummaryListing } from '../common/items/BookingSummaryListing';
import { UpdateSummaryPriceDiff } from './items/UpdateSummaryPriceDiff';

export const BookingUpdateSummary = ({
  pendingContracts,
  currentContracts,
  profile,
  intl,
  showSkeleton
}) => {
  const pendingPriceBreakdown = priceBreakdownObject(pendingContracts);

  const { masterContract: currentMasterContract } = currentContracts;
  const {
    spaceRentalContract: pendingSpaceRentalContract,
    masterContract: pendingMasterContract
  } = pendingContracts;
  const listing = pendingSpaceRentalContract?.meta?.listing;

  const {
    currency,
    guestSplitPaymentWithoutFee,
    amountPaid,
    updateDifference
  } = pendingPriceBreakdown;

  const dueNow = updateDifference?.due_now || 0;
  const totalRefund = updateDifference?.refund || 0;
  const reducedRefund = currentMasterContract?.split_payment_checkout
    ? updateDifference?.refund_accounting_for_scheduled_payment || 0
    : totalRefund;

  return (
    <div
      className='border-gray-200 padding-xl-top padding-xl-bottom'
      data-testing-id='booking-update-summary'
    >
      <div className='flex column'>
        <div className='padding-xl-left padding-xl-right'>
          <BookingSummaryListing listing={listing} intl={intl} />
          <hr className='w-100' />
        </div>

        <UpdateSummaryPriceDiff
          currentContracts={currentContracts}
          pendingContracts={pendingContracts}
          profile={profile}
          showSkeleton={showSkeleton}
        />

        <div className='padding-xl-left padding-xl-right'>
          <hr className='w-100' />
          <div className='margin-lg-bottom'>
            <span className='text-base heavier'>
              <FormattedMessage
                id='summary_payments.amount_paid'
                defaultMessage='Amount paid'
              />
            </span>
            <span
              className='pull-right heavier'
              data-testing-id='update-summary-amount-paid'
            >
              <FormattedNumber
                value={amountPaid}
                currency={currency}
                style='currency'
              />
            </span>
          </div>
          {pendingMasterContract?.split_payment_checkout &&
            currentMasterContract?.balance_split_payments && (
              <div className='margin-lg-bottom'>
                <span className='text-base heavier'>
                  <FormattedMessage
                    id='summary_payments.scheduled'
                    defaultMessage='Scheduled'
                  />
                </span>
                <span
                  className='pull-right heavier'
                  data-testing-id='update-summary-sp-amount'
                >
                  <FormattedNumber
                    value={guestSplitPaymentWithoutFee || 0}
                    currency={currency}
                    style='currency'
                  />
                </span>
                <div
                  className='text-small margin-sm-top'
                  data-testing-id='update-summary-sp-date'
                >
                  <FormattedDate
                    value={pendingMasterContract?.split_payment_charge_date}
                    year='numeric'
                    month='short'
                    day='numeric'
                  />
                </div>
              </div>
            )}

          {!!dueNow && (
            <div className='margin-lg-bottom'>
              <span className='text-base heavier'>
                <FormattedMessage
                  id='booking_update_summary.due_now'
                  defaultMessage='Due now'
                />
              </span>
              <span
                className='pull-right heavier'
                data-testing-id='update-summary-due-now'
              >
                <FormattedNumber
                  value={dueNow || 0}
                  currency={currency}
                  style='currency'
                />
              </span>
            </div>
          )}

          {!!reducedRefund && (
            <div className='margin-lg-bottom'>
              <span className='text-base heavier'>
                <FormattedMessage
                  id='booking_update_summary.potential_refund'
                  defaultMessage='Potential refund'
                />
              </span>
              <span
                className='pull-right heavier'
                data-testing-id='update-summary-refund'
              >
                <FormattedNumber
                  value={reducedRefund || 0}
                  currency={currency}
                  style='currency'
                />
              </span>
              <p className='text-small' style={{ maxWidth: '66%' }}>
                <FormattedMessage
                  id='booking_update_summary.potential_refund_details'
                  defaultMessage='Your final amount to be refunded will depend on the host’s policy.'
                />
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

BookingUpdateSummary.propTypes = {
  profile: PropTypes.object.isRequired,
  currentContracts: normalizedContractsShape,
  pendingContracts: normalizedContractsShape,
  intl: intlShape,
  showSkeleton: PropTypes.bool
};
